/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { ICmsNavItemsData, IMenuItemData, INavigationMenuViewProps } from '@msdyn365-commerce-modules/navigation-menu';
import {
    Module,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IPayLoad,
    ITelemetryContent,
    Node,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

/* VSI Customization - START */
import { generateImageUrl, getCategoryUrl } from '@msdyn365-commerce-modules/retail-actions';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import MsDyn365, { getUrlSync, IActionContext, IImageData, Image, Text } from '@msdyn365-commerce/core';
import { LoyaltyCard, LoyaltyGroup, TextValueTranslation } from '@msdyn365-commerce/retail-proxy';
import { IDobbiesNavigationMenuProps, ISubMenusData, navigationMenuSource } from './dobbies-navigation-menu.props.autogenerated';
import { IDobbiesNavigationMenuData } from './dobbies-navigation-menu.data';
import { IMenuItemDataExtended } from './dobbies-navigation-menu';
/* VSI Customization - END */

interface INavigationState {
    parentMenu?: number;
    activeMenu?: number;
    scrollClass: boolean;
    mobileViewLabelText?: string;
    /* VSI Customization - START
  when mouse moves on any submneu item, currentcategory shows its option at ID if at level 2 and previous category ID (which is actually its parent ID) if at level 3 */
    currentCategory?: number | undefined;
    // checks whether back button is clicked or not on mobile menu
    isBackBtnClicked?: boolean;
    categoryName?: string;
    categoryImage?: string | null;
    categoryImageLink?: string;
    dobbiesClubLink?: string; // Check if user is club or club plus member, clicking on Dobbies club will navigate to loyalty page otherwise to dobbies marketing or as configured in CMS page
    /* VSI Customization - END */
    navigationSearch: boolean;
}

const NAMES_TO_HIDE = ['Edits', 'CS-'];

const shouldHideMenuItem = (menuName: string | undefined): boolean => {
    if (menuName === undefined) {
        return true;
    }
    return NAMES_TO_HIDE.some(name => menuName.startsWith(name));
};

/**
 *
 * NavigationMenuView component
 * @extends {React.PureComponent<INavigationMenuViewProps>}
 */
export class NavigationMenuView extends React.PureComponent<
    INavigationMenuViewProps & IDobbiesNavigationMenuProps<IDobbiesNavigationMenuData>,
    INavigationState
> {
    public static isBackTrack: boolean = false;
    public static CMSItemIdInit: number = 1000;
    public static RSItemIdInit: number = 5000;
    public supportedLevel: number = 3;
    public currentLevel: number = 0;
    /* VSI Customization - START - 13/11/20 */
    // Tells current active level on mobile
    private currentLevelMobile: number = 0;
    private menuItemData: IMenuItemDataExtended[];
    // private isRetail: boolean = true;
    private isCMS: boolean = true;
    /* VSI Customization - END */
    private menuNode: React.RefObject<HTMLUListElement>;
    private telemetryContent: ITelemetryContent;
    private payLoad: IPayLoad;
    //private documentRef: Document | undefined;

    private mouseDownRefs: ((e: any) => void)[] = [];
    private navRef: React.RefObject<HTMLDivElement>;

    public static getFriendlyName(locale: string, nameTranslations?: TextValueTranslation[]): string | undefined {
        let nameTranslation: TextValueTranslation | undefined;
        if (locale && nameTranslations && nameTranslations.length > 0) {
            nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
        }

        return nameTranslation && nameTranslation.Text;
    }

    constructor(props: INavigationMenuViewProps & IDobbiesNavigationMenuProps<IDobbiesNavigationMenuData>, state: INavigationState) {
        super(props);
        this.menuNode = React.createRef();
        this.navRef = React.createRef();
        // this.menuItemData = [];
        this.menuItemData = [];
        this._setNavigationSource(this.props.config && this.props.config.navigationMenuSource);
        this.state = {
            navigationSearch: true,
            activeMenu: undefined,
            mobileViewLabelText: '',
            parentMenu: undefined,
            scrollClass: false,
            isBackBtnClicked: false
        };
        this._closeSubmenu = this._closeSubmenu.bind(this);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.payLoad = getPayloadObject('click', this.telemetryContent, '', '');
    }

    public componentDidMount(): void {
        //this.documentRef = document;

        if (document.body) {
            this.mouseDownRefs.push(this._handleClickOutside);
            this.mouseDownRefs.push(this._handleMouseHover(undefined));
            this.mouseDownRefs.push(this._handleLinkClick(undefined));
            this.mouseDownRefs.forEach(ref => document.body.addEventListener('mousedown', ref));
        }
        window.addEventListener('scroll', this._mainMenuScroll);

        // Get Customer Loyalty card details if he/she is authenticated and state is not set yet
        this.props.data.loyaltyCard.then(loyaltyCard => {
            const loyaltyGroup = loyaltyCard && loyaltyCard.LoyaltyGroups && loyaltyCard.LoyaltyGroups[0];
            const tierId = loyaltyGroup && this._getTierId(loyaltyGroup);
            const redirectionLink = loyaltyCard && tierId && this._getRedirectionLink(loyaltyCard, tierId);
            this.setState({ dobbiesClubLink: redirectionLink });
        });
    }

    public componentWillUnmount(): void {
        this.mouseDownRefs.forEach(ref => document.body.removeEventListener('mousedown', ref));
        window.addEventListener('scroll', this._mainMenuScroll);
    }

    /* VSI CUSTOMIZATION - START  - 18/09/2023 */
    private _constructSecondLevelStaticMenus(id: number, linkText: string): IMenuItemData[] {
        const staticMenuItems: IMenuItemData[] = [];
        /* VSI Customization - START - 8/31/22  /** adding another menu   */
        if (this.isCMS && this.props.config.subMenuStaticMenus && this.props.config.subMenuStaticMenus.length > 0) {
            this.props.config.subMenuStaticMenus.map((item: ICmsNavItemsData, idx: number) => {
                if (linkText === item.linkText) {
                    item.subMenus?.map(submenu => {
                        staticMenuItems.push(this._constructMenuItemData(undefined, submenu, false, idx + 1));
                    });
                }
            });
        }
        return staticMenuItems;
    }
    private _getUrlFromConfigOfNavItem(linkText: string): string {
        var urlFromConfig: string = '';
        if (this.isCMS && this.props.config.subMenuStaticMenus && this.props.config.subMenuStaticMenus.length > 0) {
            this.props.config.subMenuStaticMenus.map((item: ICmsNavItemsData, idx: number) => {
                if (linkText === item.linkText) {
                    urlFromConfig = item.linkUrl?.destinationUrl ? item.linkUrl?.destinationUrl : '';
                }
            });
        }
        return urlFromConfig;
    }
    /* VSI CUSTOMIZATION - END CUSTOMIZATION - 18/09/2023 */
    private _getCategoryScheduledImageUrl = (): string | undefined => {
        let scheduledImageUrl: string | undefined = '';
        if (this.state.categoryName && this.props.config.menuImagesSchedule) {
            for (const imageSchedule of this.props.config.menuImagesSchedule) {
                if (imageSchedule.menuName === this.state.categoryName) {
                    try {
                        const now = new Date();
                        const dateTimeSplittedStartDate = imageSchedule.imageShowStartDate.split(' ');
                        const startDateMonth = dateTimeSplittedStartDate[0].split('-')[1];
                        const startDateDay = dateTimeSplittedStartDate[0].split('-')[0];
                        const startDateYear = dateTimeSplittedStartDate[0].split('-')[2];
                        const startDateHour = dateTimeSplittedStartDate[1].split(':')[0];
                        const startDateMinutes = dateTimeSplittedStartDate[1].split(':')[1];
                        let imageShowingStartDate = new Date(
                            `${startDateMonth}-${startDateDay}-${startDateYear} ${startDateHour}:${startDateMinutes}:00`
                        );
                        if (this.getMobileOperatingSystem() === 'iOS') {
                            imageShowingStartDate = new Date(
                                parseInt(startDateYear.startsWith('20') ? startDateYear : `20${startDateYear}`, 10),
                                parseInt(startDateMonth, 10) - 1,
                                parseInt(startDateDay, 10),
                                parseInt(startDateHour, 10),
                                parseInt(startDateMinutes, 10),
                                0
                            );
                        }
                        const dateTimeSplittedEndDate = imageSchedule.imageShowEndDate.split(' ');
                        const endDateMonth = dateTimeSplittedEndDate[0].split('-')[1];
                        const endDateDay = dateTimeSplittedEndDate[0].split('-')[0];
                        const endDateYear = dateTimeSplittedEndDate[0].split('-')[2];
                        const endDateHour = dateTimeSplittedEndDate[1].split(':')[0];
                        const endDateMinutes = dateTimeSplittedEndDate[1].split(':')[1];
                        let imageShowEndDate = new Date(`${endDateMonth}-${endDateDay}-${endDateYear} ${endDateHour}:${endDateMinutes}:00`);
                        if (this.getMobileOperatingSystem() === 'iOS') {
                            imageShowEndDate = new Date(
                                parseInt(endDateYear.startsWith('20') ? endDateYear : `20${endDateYear}`, 10),
                                parseInt(endDateMonth, 10) - 1,
                                parseInt(endDateDay, 10),
                                parseInt(endDateHour, 10),
                                parseInt(endDateMinutes, 10),
                                0
                            );
                        }
                        if (now.getTime() <= imageShowEndDate.getTime() && now.getTime() >= imageShowingStartDate.getTime()) {
                            scheduledImageUrl = imageSchedule.imageUrl;
                            return scheduledImageUrl;
                        }
                    } catch (ex) {
                        return undefined;
                    }
                }
            }
        }
        return scheduledImageUrl;
    };
    public render(): JSX.Element | null {
        this.menuItemData = this.props.menuItemData; //this._mapData();
        if (this.props.isMobileView) {
            if (MsDyn365.isBrowser && window) {
                const body = document.getElementsByTagName('body')[0];
                if (body && !body.classList.contains('mobile')) {
                    body.classList.remove('pc');
                    body.classList.add('mobile');
                }
            }
        } else {
            if (MsDyn365.isBrowser && window) {
                const body = document.getElementsByTagName('body')[0];
                if (body && !body.classList.contains('pc')) {
                    body.classList.remove('mobile');
                    body.classList.add('pc');
                }
            }
        }
        const {
            isMobileView,
            MenuList,
            MobileBackButton,
            MobileDescriptionContainer,
            MobileDescriptionLabel,
            Navigation,
            config: { menuHeading }
        } = this.props;
        this.currentLevel = 1;
        const searchSlot = this._getSlot('search');
        const search = (searchSlot && searchSlot.length && searchSlot) || [];

        return (
            /* Below line updated for Sticky Navigation */
            <div ref={this.navRef}>
                <Module
                    {...Navigation}
                    className={classnames(
                        Navigation.className,
                        isMobileView && this.state.activeMenu !== undefined ? 'child' : 'parent',
                        this.state.scrollClass ? 'sticky-nav' : ''
                    )}
                >
                    {/* VSI Customization - START
         Mobile menu heading */}
                    {isMobileView && this.state.activeMenu === undefined && (
                        <Node tag='div' className={classnames('ms-nav heading')}>
                            <Text tag='h3' className={''} text={menuHeading} />
                        </Node>
                    )}
                    {/* VSI Customization - END */}
                    <Node {...MenuList} ref={this.menuNode} tabIndex='-1'>
                        {isMobileView && this.state.activeMenu !== undefined && (
                            <Node {...MobileDescriptionContainer}>
                                <Node {...MobileBackButton} onClick={this._handleGoBack()} />
                                <Node {...MobileDescriptionLabel}>{this.state.mobileViewLabelText}</Node>
                            </Node>
                        )}

                        {this.state.navigationSearch ? this._renderDisplay() : ''}
                        {this.state.scrollClass &&
                            (this.state.navigationSearch ? (
                                <div className='search-icon' onClick={() => this.setState({ navigationSearch: false })}></div>
                            ) : (
                                <>
                                    <Node className='ms-header__search-container'>{search} </Node>
                                    <div className='search-cross-icon' onClick={() => this.setState({ navigationSearch: true })}></div>
                                </>
                            ))}
                    </Node>
                </Module>
            </div>
        );
    }

    private _getSlot(slotName: string): React.ReactNode[] | null {
        const { slots } = this.props;
        return (slots && slots[slotName]! && slots[slotName].length && slots[slotName]!) || null;
    }

    private _renderDisplay(): JSX.Element[] {
        const { ListItem, isMobileView } = this.props;
        const { activeMenu } = this.state;
        const { menuItemData } = this;
        const menuItemList: JSX.Element[] = [];

        if (isMobileView && activeMenu !== undefined && menuItemData.length > 0) {
            let menuItem: IMenuItemDataExtended = {};
            for (let i = 0; i < menuItemData.length; i++) {
                if (menuItemData[i] && menuItemData[i].id === activeMenu) {
                    menuItem = menuItemData[i];
                    !NavigationMenuView.isBackTrack
                        ? this.setState({ parentMenu: undefined })
                        : this.setState({ parentMenu: undefined, mobileViewLabelText: menuItemData[i].linkText });
                    break;
                }
                menuItem = this._getFromSubMenu(menuItemData[i]) as IMenuItemDataExtended;
                if (menuItem && menuItem.id === activeMenu) {
                    break;
                }
            }
            /* VSI Customization - START - 13/11/20 */
            if (menuItem) {
                // if user is on level < supported level, add 1 in currentLevelMobile
                // but if user clicks on back button and go to previous menu, currentLevelMobile will not be incremented
                this.currentLevelMobile < this.supportedLevel && this.state.isBackBtnClicked === false && this.currentLevelMobile++;
                menuItemList.push(
                    <Node key={menuItem.id} {...ListItem} className={`${ListItem.className}  ${menuItem.custom_classname}`}>
                        {' '}
                        {this._createMenuItemList(menuItem)}{' '}
                    </Node>
                );
            }
            /* VSI Customization - END */
        } else {
            /* VSI Customization - START -13/11/20 */
            // if user is on first/root navigation menu level
            this.currentLevelMobile = 0;
            /* VSI Customization - END */
            menuItemData.forEach((item: IMenuItemDataExtended, index: number) => {
                menuItemList.push(
                    <Node key={index} {...ListItem} className={`${ListItem.className}  ${item.custom_classname}`}>
                        {this._createMenuItemList(item)}
                    </Node>
                );
            });
        }

        return menuItemList;
    }
    /* VSI Customization - START - 17/06/21 - Copied following functions from tsx file to display all option on 3rd level */
    // private _mapData(): IMenuItemData[] {
    //     const { data, config } = this.props;
    //     const showCannedSearches = this.props.context.request.query?.showCannedSearches;
    //     const mergedData: IMenuItemData[] = [];
    //     /* VSI Customization - START - 8/31/22  /** adding another menu   */
    //     if (this.isCMS && config.cmsNavItemsNew && config.cmsNavItemsNew.length > 0) {
    //         config.cmsNavItemsNew.map((item: ICmsNavItemsData, idx: number) =>
    //             mergedData.push(this._constructMenuItemData(undefined, item, false, idx + 1))
    //         );
    //     }
    //     /* VSI Customization - END - 8/31/22  /** adding another menu   */

    //     if (
    //         this.isRetail &&
    //         config.navigationMenuSource === 'all' &&
    //         data &&
    //         data.categories &&
    //         data.categories.result &&
    //         data.categories.result.length > 0
    //     ) {
    //         data.categories.result.map((item: CategoryHierarchy, index) => {
    //             if (
    //                 item.Name === 'Sale' ||
    //                 (this.props.config.hideMenuItemPrefix && item.NeutralizedName?.startsWith(this.props.config.hideMenuItemPrefix))
    //             ) {
    //                 return;
    //             }
    //             (showCannedSearches || !shouldHideMenuItem(item.Name)) &&
    //                 mergedData.push(this._constructMenuItemData(item, undefined, true, index, true));
    //         });
    //     }

    //     if (this.isCMS && config.cmsNavItems && config.cmsNavItems.length > 0) {
    //         config.cmsNavItems.map((item: ICmsNavItemsData, idx: number) =>
    //             mergedData.push(this._constructMenuItemData(undefined, item, false, idx + 1))
    //         );
    //     }
    //     return this._constructTopMenuItemData(mergedData);
    // }

    // private _constructTopMenuItemData(mergedData: IMenuItemData[]): IMenuItemData[] {
    //     const enableTopLevelMenu = this.props.config.enabletopMenu || false;
    //     if (!enableTopLevelMenu) {
    //         return mergedData;
    //     }
    //     const mergedRootMenuData: IMenuItemData[] = [];
    //     const topMenu = {
    //         id: 1,
    //         linkText: this.props.config.rootMenuNavigation || '...',
    //         linkURL: '#',
    //         imageSource: undefined,
    //         subMenu: mergedData
    //     } as IMenuItemData;
    //     mergedRootMenuData.push(topMenu);

    //     return mergedRootMenuData;
    // }

    /* VSI CUSTOMIZATION - START CUSTOMIZATION - 08/11/2023 */
    private menuExistsInRedundantMobileMenusConfig(menuText?: string): string {
        if (this.props.config.removeExtraSubMenusOnMobile && this.props.config.removeExtraSubMenusOnMobile.length) {
            for (let i = 0; i < this.props.config.removeExtraSubMenusOnMobile.length; i++) {
                if (menuText === this.props.config.removeExtraSubMenusOnMobile[i]) {
                    return menuText;
                }
            }
        }
        return '';
    }
    /* VSI CUSTOMIZATION - END CUSTOMIZATION - 08/11/2023 */

    // tslint:disable-next-line: cyclomatic-complexity
    private _constructMenuItemData(
        retailItem?: CategoryHierarchy,
        cmsItem?: ICmsNavItemsData,
        isRetail?: boolean,
        idx?: number,
        addedAllMenu?: boolean
    ): IMenuItemData {
        const locale =
            this.props.context && this.props.context.request && this.props.context.request.locale
                ? this.props.context.request.locale
                : 'en-us';
        const imageSouce = retailItem && retailItem.Images && retailItem.Images[0].Uri ? retailItem.Images[0].Uri : 'empty';
        /* VSI CUSTOMIZATION - START - 18/09/2023 */
        const friendlyName = NavigationMenuView.getFriendlyName(locale, retailItem && retailItem.NameTranslations) || '';
        const urlFromConfig = this._getUrlFromConfigOfNavItem(friendlyName);
        if (isRetail) {
            if (retailItem?.Children && retailItem.Children.length) {
                retailItem.Children = retailItem.Children.filter(
                    ric => !(this.props.config.hideMenuItemPrefix && ric.NeutralizedName?.startsWith(this.props.config.hideMenuItemPrefix))
                );
            }
            return {
                id: NavigationMenuView.RSItemIdInit++,
                linkText: friendlyName,
                linkURL: urlFromConfig ? urlFromConfig : retailItem && retailItem.Url,
                imageSource: imageSouce,
                subMenu: this._getNestedChildren(retailItem, imageSouce, addedAllMenu).concat(
                    this._constructSecondLevelStaticMenus(
                        retailItem?.RecordId ? retailItem.RecordId : -1,
                        retailItem?.Name ? retailItem.Name : ''
                    )
                )
            } as IMenuItemData;
        } else {
            const isMobile = this.props.isMobileView;
            if (isMobile) {
                const menuText = this.menuExistsInRedundantMobileMenusConfig(cmsItem?.linkText);
                if (menuText) {
                    const subMenu = this._getCMSNestedChildren(cmsItem);
                    if (subMenu.length === 1) {
                        const subMenuItem = subMenu[0];
                        if (subMenuItem.linkText === menuText) {
                            return {
                                id: NavigationMenuView.CMSItemIdInit++,
                                linkText: (cmsItem && cmsItem.linkText) || '',
                                linkURL: (cmsItem && cmsItem.linkUrl && cmsItem.linkUrl.destinationUrl) || '',
                                imageDestinationURL: (cmsItem && cmsItem.imageLink && cmsItem.imageLink.destinationUrl) || '',
                                imageSource: (cmsItem && cmsItem.image && cmsItem.image.src) || '',
                                subMenu: subMenuItem.subMenu
                            } as IMenuItemData;
                        }
                    }
                }
            }
            return {
                id: NavigationMenuView.CMSItemIdInit++,
                linkText: (cmsItem && cmsItem.linkText) || '',
                linkURL: (cmsItem && cmsItem.linkUrl && cmsItem.linkUrl.destinationUrl) || '',
                imageDestinationURL: (cmsItem && cmsItem.imageLink && cmsItem.imageLink.destinationUrl) || '',
                imageSource: (cmsItem && cmsItem.image && cmsItem.image.src) || '',
                subMenu: this._getCMSNestedChildren(cmsItem)
            } as IMenuItemData;
        }
    }

    private _getNestedChildren(category?: CategoryHierarchy, imageSourceUrl?: string, addedAllMenu: boolean = false): IMenuItemData[] {
        const nestedChildren: IMenuItemData[] = [];
        const showCannedSearches = this.props.context.request.query?.showCannedSearches;
        if (!category || !category.Children || !category.Children.length) {
            return [];
        }
        if (!this.props.config.enabletopMenu && addedAllMenu) {
            const locale =
                this.props.context && this.props.context.request && this.props.context.request.locale
                    ? this.props.context.request.locale
                    : 'en-us';
            const friendlyName = NavigationMenuView.getFriendlyName(locale, category && category.NameTranslations) || '';
            nestedChildren.push({
                linkText: this.props.resources.allCategoryMenuText,
                imageSource: imageSourceUrl,
                linkURL:
                    this._getUrlFromConfigOfNavItem(friendlyName) ||
                    getCategoryUrl(category, (this.props.context as unknown) as IActionContext, {})
            });
        }
        category.Children.map((item: CategoryHierarchy, index) => {
            (showCannedSearches || !shouldHideMenuItem(item.Name)) &&
                nestedChildren.push(this._constructMenuItemData(item, undefined, true, index, true));
        });
        return nestedChildren;
    }

    private _getCMSNestedChildren(category?: ICmsNavItemsData): IMenuItemData[] {
        const nestedChildren: IMenuItemData[] = [];

        if (!category || !category.subMenus || !category.subMenus.length) {
            return [];
        }

        category.subMenus.map((item: ISubMenusData, idx: number) => {
            nestedChildren.push(this._constructMenuItemData(undefined, item, false, idx));
        });
        return nestedChildren;
    }

    private _setNavigationSource = (sourceType?: navigationMenuSource): void => {
        switch (sourceType) {
            case navigationMenuSource.retailServerOnly:
                // this.isRetail = true;
                this.isCMS = false;
                break;
            case navigationMenuSource.cmsOnly:
                // this.isRetail = false;
                this.isCMS = true;
                break;
            case navigationMenuSource.all:
                // this.isRetail = true;
                this.isCMS = true;
                break;
            default:
        }
    };
    /* VSI Customization - END - 17/04/21 */
    private _getFromSubMenu(item?: IMenuItemDataExtended): IMenuItemDataExtended | null {
        const subMenus = item && item.subMenu;
        if (subMenus && subMenus.length > 0) {
            for (let i = 0; i <= subMenus.length - 1; i++) {
                if (subMenus[i].id === this.state.activeMenu) {
                    !NavigationMenuView.isBackTrack
                        ? this.setState({ parentMenu: item && item.id })
                        : this.setState({ parentMenu: item && item.id, mobileViewLabelText: subMenus[i].linkText });
                    return subMenus[i];
                }
                const found = this._getFromSubMenu(subMenus[i]);
                if (found) {
                    return found;
                }
            }
        }
        this.props.context.telemetry.error('Submenu content is empty, module wont render.');
        return null;
    }
    private _renderMobileSubMenu(menuItemData: IMenuItemDataExtended): JSX.Element | null {
        return (
            <React.Fragment>
                {this._renderButtonMenuItem(menuItemData)}
                {this._renderSubMenu(menuItemData.subMenu, menuItemData.id, undefined, menuItemData.linkText)}
            </React.Fragment>
        );
    }

    private _createMenuItemList(menuItemData: IMenuItemDataExtended): JSX.Element | null {
        const {
            isMobileView,
            config: { dobbiesClubNavItemText },
            context: {
                request: {
                    user: { isAuthenticated }
                }
            }
        } = this.props;
        if (menuItemData && menuItemData.subMenu && menuItemData.subMenu.length > 0) {
            if (isMobileView && this.state.activeMenu !== undefined) {
                return this._renderSubMenu(
                    menuItemData.subMenu as IMenuItemDataExtended[],
                    menuItemData.id,
                    undefined,
                    menuItemData.linkText
                );
            } else {
                /* VSI Customization - START */
                if (isMobileView) {
                    return this._renderMobileSubMenu(menuItemData);
                }
                return (
                    <React.Fragment>
                        {this._renderButtonMenuItem(menuItemData)}
                        <Node
                            tag='div'
                            className={classnames(this.props.className, 'submenu')}
                            id={menuItemData.id}
                            //style={{ height: this._getSubMenuLength(menuItemData.id) }}
                        >
                            {this._renderSubMenu(menuItemData.subMenu as IMenuItemDataExtended[], menuItemData.id, undefined)}
                        </Node>
                        <Node className='extra-menu-items' tag='div' />
                    </React.Fragment>
                );
                /* VSI Customization - END */
            }
        } else if (menuItemData && menuItemData.linkText && menuItemData.linkURL && menuItemData.linkURL.length > 0) {
            /* VSI Customization - START - 11/11/20 */
            // Check if user is club or club plus member, clicking on Dobbies club will navigate to loyalty page otherwise to dobbies marketing or as configured in CMS page
            let updatedMenuItemData: IMenuItemDataExtended = menuItemData;
            if (isAuthenticated && menuItemData.linkText?.toLowerCase() === dobbiesClubNavItemText?.toLowerCase()) {
                updatedMenuItemData = this.conditionalRoutingMenuItem(menuItemData);
            }
            /* Displayed static menu items as buttons in desktop view and as link menu items in mobile/iPad view */
            if (isMobileView) {
                return this._renderLinkMenuItem(updatedMenuItemData);
            }
            return this._renderButtonMenuItem(updatedMenuItemData);
            /* VSI Customization - END */
        } else if (menuItemData && menuItemData.linkText && !menuItemData.linkURL) {
            return this._renderSpanMenuItem(menuItemData);
        }
        this.props.context.telemetry.error('Navigation menu content is empty, module wont render.');
        return null;
    }

    /* VSI Customization - START - 07/04/21 - Conditional routing of dobbies club navigation menu item */

    // Route conditionally on clicking Dobbies Club nav item, it updated menu item url and returns updated menuItemData
    private conditionalRoutingMenuItem = (menuItemData: IMenuItemDataExtended): IMenuItemDataExtended => {
        const { dobbiesClubLink } = this.state;

        if (dobbiesClubLink) {
            const updatedMenuItem = menuItemData;
            updatedMenuItem.linkURL = dobbiesClubLink;
            return updatedMenuItem;
        }
        return menuItemData;
    };

    // Provide the TierId of user's loyalty
    private _getTierId = (loyaltyGroup: LoyaltyGroup | undefined) => {
        // highestLoyaltCardTier
        const highestActiveLoyaltyCardTier = loyaltyGroup && loyaltyGroup.HighestActiveLoyaltyCardTier;
        const loyaltyCardTiers = loyaltyGroup && loyaltyGroup.LoyaltyCardTiers;
        if (loyaltyCardTiers && loyaltyCardTiers?.length > 0) {
            for (const [_, loyaltyCardTier] of loyaltyCardTiers.entries()) {
                if (loyaltyCardTier.RecordId === highestActiveLoyaltyCardTier) {
                    return loyaltyCardTier.TierId;
                }
            }
        }
        return;
    };

    // Redirects the user based on certain criteria defined inside function
    private _getRedirectionLink = (loyaltyCard: LoyaltyCard, tierId: string | undefined) => {
        const {
            config: { clubFreeTierText, clubPlusTierText }
        } = this.props;

        if (!loyaltyCard.CardNumber || !tierId || !clubFreeTierText || !clubPlusTierText) {
            return undefined;
        }

        tierId = tierId.toLowerCase().trim();
        // If user is already dobbies club free/plus member and clicks on dobbies club nav item, navigate him to loyalty page
        if (tierId === clubFreeTierText.toLowerCase() || tierId === clubPlusTierText.toLowerCase()) {
            return getUrlSync('loyalty', this.props.context.actionContext);
        }

        return undefined;
    };

    private getMobileOperatingSystem(): string {
        // Get the user agent string
        const userAgent: string = navigator.userAgent || navigator.vendor || (window as any).opera;

        // Check for Windows Phone first because its user agent string may contain "Android"
        if (/windows phone/i.test(userAgent)) {
            return 'Windows Phone';
        }

        // Check for Android devices
        if (/android/i.test(userAgent)) {
            return 'Android';
        }

        // Check for iOS devices (iPad, iPhone, iPod)
        // The condition !window.MSStream ensures that it's not a Windows device
        if ((/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) || userAgent.indexOf('Mac OS X') > 0) {
            return 'iOS';
        }

        // If no matches, return 'unknown'
        return 'unknown';
    }

    /* VSI Customization - END */
    private _getCategoryImage = (categoryImage: string): React.ReactNode | null => {
        const categoryImageUrl = generateImageUrl(`${categoryImage}`, this.props.context.actionContext.requestContext.apiSettings);
        if (this.props.isMobileView && this.props.config.menuImagesSchedule) {
            for (const imageSchedule of this.props.config.menuImagesSchedule) {
                if (imageSchedule.menuName === this.state.categoryName && imageSchedule.isForMobile) {
                    try {
                        const now = new Date();
                        const dateTimeSplittedStartDate = imageSchedule.imageShowStartDate.split(' ');
                        const startDateMonth = dateTimeSplittedStartDate[0].split('-')[1];
                        const startDateDay = dateTimeSplittedStartDate[0].split('-')[0];
                        const startDateYear = dateTimeSplittedStartDate[0].split('-')[2];
                        const startDateHour = dateTimeSplittedStartDate[1].split(':')[0];
                        const startDateMinutes = dateTimeSplittedStartDate[1].split(':')[1];
                        let imageShowingStartDate = new Date(
                            `${startDateMonth}-${startDateDay}-${startDateYear} ${startDateHour}:${startDateMinutes}:00`
                        );
                        if (this.getMobileOperatingSystem() === 'iOS') {
                            imageShowingStartDate = new Date(
                                parseInt(startDateYear.startsWith('20') ? startDateYear : `20${startDateYear}`, 10),
                                parseInt(startDateMonth, 10) - 1,
                                parseInt(startDateDay, 10),
                                parseInt(startDateHour, 10),
                                parseInt(startDateMinutes, 10),
                                0
                            );
                        }
                        const dateTimeSplittedEndDate = imageSchedule.imageShowEndDate.split(' ');
                        const endDateMonth = dateTimeSplittedEndDate[0].split('-')[1];
                        const endDateDay = dateTimeSplittedEndDate[0].split('-')[0];
                        const endDateYear = dateTimeSplittedEndDate[0].split('-')[2];
                        const endDateHour = dateTimeSplittedEndDate[1].split(':')[0];
                        const endDateMinutes = dateTimeSplittedEndDate[1].split(':')[1];
                        let imageShowEndDate = new Date(`${endDateMonth}-${endDateDay}-${endDateYear} ${endDateHour}:${endDateMinutes}:00`);
                        if (this.getMobileOperatingSystem() === 'iOS') {
                            imageShowEndDate = new Date(
                                parseInt(endDateYear.startsWith('20') ? endDateYear : `20${endDateYear}`, 10),
                                parseInt(endDateMonth, 10) - 1,
                                parseInt(endDateDay, 10),
                                parseInt(endDateHour, 10),
                                parseInt(endDateMinutes, 10),
                                0
                            );
                        }
                        if (now.getTime() <= imageShowEndDate.getTime() && now.getTime() >= imageShowingStartDate.getTime()) {
                            const scheduledImageUrl = generateImageUrl(
                                `${imageSchedule.menuImageName}`,
                                this.props.context.actionContext.requestContext.apiSettings
                            );
                            const ImageData: IImageData = { src: scheduledImageUrl };
                            return (
                                <Image
                                    className='ms-nav-image__item'
                                    {...ImageData}
                                    gridSettings={this.props.context.request.gridSettings!}
                                    {...this.props.config.imageSettings}
                                    loadFailureBehavior='default'
                                    role='tabpanel'
                                    id={`${categoryImage}__categoryImage`}
                                />
                            );
                        }
                    } catch (ex) {
                        console.log('Error', ex);
                    }
                }
            }
            return null;
        }

        if (this.state.categoryName && this.props.config.menuImagesSchedule) {
            for (const imageSchedule of this.props.config.menuImagesSchedule) {
                if (imageSchedule.menuName === this.state.categoryName && !imageSchedule.isForMobile) {
                    try {
                        const now = new Date();
                        const dateTimeSplittedStartDate = imageSchedule.imageShowStartDate.split(' ');
                        const startDateMonth = dateTimeSplittedStartDate[0].split('-')[1];
                        const startDateDay = dateTimeSplittedStartDate[0].split('-')[0];
                        const startDateYear = dateTimeSplittedStartDate[0].split('-')[2];
                        const startDateHour = dateTimeSplittedStartDate[1].split(':')[0];
                        const startDateMinutes = dateTimeSplittedStartDate[1].split(':')[1];
                        let imageShowingStartDate = new Date(
                            `${startDateMonth}-${startDateDay}-${startDateYear} ${startDateHour}:${startDateMinutes}:00`
                        );
                        if (this.getMobileOperatingSystem() === 'iOS') {
                            imageShowingStartDate = new Date(
                                parseInt(startDateYear.startsWith('20') ? startDateYear : `20${startDateYear}`, 10),
                                parseInt(startDateMonth, 10) - 1,
                                parseInt(startDateDay, 10),
                                parseInt(startDateHour, 10),
                                parseInt(startDateMinutes, 10),
                                0
                            );
                        }
                        const dateTimeSplittedEndDate = imageSchedule.imageShowEndDate.split(' ');
                        const endDateMonth = dateTimeSplittedEndDate[0].split('-')[1];
                        const endDateDay = dateTimeSplittedEndDate[0].split('-')[0];
                        const endDateYear = dateTimeSplittedEndDate[0].split('-')[2];
                        const endDateHour = dateTimeSplittedEndDate[1].split(':')[0];
                        const endDateMinutes = dateTimeSplittedEndDate[1].split(':')[1];
                        let imageShowEndDate = new Date(`${endDateMonth}-${endDateDay}-${endDateYear} ${endDateHour}:${endDateMinutes}:00`);
                        if (this.getMobileOperatingSystem() === 'iOS') {
                            imageShowEndDate = new Date(
                                parseInt(endDateYear.startsWith('20') ? endDateYear : `20${endDateYear}`, 10),
                                parseInt(endDateMonth, 10) - 1,
                                parseInt(endDateDay, 10),
                                parseInt(endDateHour, 10),
                                parseInt(endDateMinutes, 10),
                                0
                            );
                        }
                        if (now.getTime() <= imageShowEndDate.getTime() && now.getTime() >= imageShowingStartDate.getTime()) {
                            const scheduledImageUrl = generateImageUrl(
                                `${imageSchedule.menuImageName}`,
                                this.props.context.actionContext.requestContext.apiSettings
                            );
                            const ImageData: IImageData = { src: scheduledImageUrl };
                            return (
                                <Image
                                    className='ms-nav-image__item'
                                    {...ImageData}
                                    gridSettings={this.props.context.request.gridSettings!}
                                    {...this.props.config.imageSettings}
                                    loadFailureBehavior='default'
                                    role='tabpanel'
                                    id={`${categoryImage}__categoryImage`}
                                />
                            );
                        }
                    } catch (ex) {
                        if (categoryImageUrl !== undefined) {
                            const ImageData: IImageData = { src: categoryImageUrl };
                            return (
                                <Image
                                    className='ms-nav-image__item'
                                    {...ImageData}
                                    gridSettings={this.props.context.request.gridSettings!}
                                    {...this.props.config.imageSettings}
                                    loadFailureBehavior='default'
                                    role='tabpanel'
                                    id={`${categoryImage}__categoryImage`}
                                />
                            );
                        }
                    }
                }
            }
        }
        if (categoryImageUrl !== undefined) {
            const ImageData: IImageData = { src: categoryImageUrl };
            return (
                <Image
                    className='ms-nav-image__item'
                    {...ImageData}
                    gridSettings={this.props.context.request.gridSettings!}
                    {...this.props.config.imageSettings}
                    loadFailureBehavior='default'
                    role='tabpanel'
                    id={`${categoryImage}__categoryImage`}
                />
            );
        }
        return null;
    };

    private _updateCategoryImage = (categoryImage: string, selectedCategoryName: string) => () => {
        /* VSI Customization - START - 16/03/21 */
        // Get category image data matched with currenct category
        const {
            config: { categoryImageLinks }
        } = this.props;
        const selectedCategory =
            categoryImageLinks &&
            categoryImageLinks.find(category => {
                const categoryName = category.categoryName && category.categoryName.trim().toLowerCase();
                return selectedCategoryName.toLowerCase().trim() === categoryName;
            });
        const categoryLink = selectedCategory && selectedCategory.categoryImageLink && selectedCategory.categoryImageLink.destinationUrl;
        /* VSI Customization - END */
        this.setState({
            categoryName: selectedCategory?.categoryName,
            categoryImage: categoryImage !== '' ? categoryImage : 'empty',
            categoryImageLink: categoryImage !== '' ? categoryLink : undefined
        });
    };

    private _renderSubMenu(
        subMenus?: IMenuItemDataExtended[],
        activeMenu?: number,
        isChild?: boolean,
        parentName?: string
    ): JSX.Element | null {
        const {
            isMobileView,
            ListItem,
            MenuList,
            config: { showOptionAll }
        } = this.props;
        /* VSI Customization - START */
        /* Added isMobileView condition to check this condition just for mobile to fill submenu by default in case of desktop */
        if (isMobileView && activeMenu && this.state.activeMenu !== activeMenu) {
            this.props.context.telemetry.error('Navigation Active menu content is empty, module wont render.');
            return null;
        }
        /* VSI Customization - END */
        if (!subMenus || subMenus.length === 0) {
            this.props.context.telemetry.error('Navigation Submenu content is empty, module wont render.');
            return null;
        }
        /* VSI Customization - START - 02/11/20 */
        let _subMenus: IMenuItemData[];
        if (isChild === undefined && !showOptionAll) {
            if (!isMobileView) {
                // Second level menu will not include option 'All' on desktop
                _subMenus =
                    subMenus &&
                    subMenus.filter(subItem => {
                        return subItem.linkText && subItem.linkText.toLowerCase() !== 'all';
                    });
            } else {
                // display parentName instead of all in third level on desktop
                let allOptionFound = false;
                _subMenus = subMenus;
                _subMenus &&
                    _subMenus.map(submenu => {
                        if (!allOptionFound && submenu.linkText && submenu.linkText.toLowerCase() === 'all') {
                            submenu.linkText = `${submenu.linkText} ${parentName}`;
                            allOptionFound = true;
                        }
                    });
            }
        } else {
            // Third level menu
            // on desktop, do not show the all category menu on the third level
            _subMenus = subMenus;
            _subMenus =
                subMenus &&
                subMenus.filter(subItem => {
                    return subItem.linkText && subItem.linkText.toLowerCase() !== 'all';
                });
        }
        /* VSI Customization - END */
        let levelClassName: string = '';
        const menuOptions =
            _subMenus &&
            _subMenus.map((option: IMenuItemDataExtended, idx: number) => {
                let isActiveOption = option.id !== undefined && this.state.currentCategory === option.id;
                isActiveOption = isActiveOption && 'id' in option; // if any option doesn't have id value, its submenu will not be created
                const hasOptions = (option.subMenu && option.subMenu.length > 0) || false;
                const renderChildMenu = isMobileView ? hasOptions : isActiveOption;

                let menuItem: JSX.Element | null;
                if (hasOptions && isMobileView && this.currentLevelMobile < this.supportedLevel - 1) {
                    menuItem = this._renderButtonMenuItem(option, activeMenu, idx);
                } else {
                    menuItem = option.linkURL
                        ? this._renderLinkMenuItem(option, idx, activeMenu, isChild, parentName)
                        : this._renderSpanMenuItem(option);
                }
                let subMenu;
                if (renderChildMenu === isActiveOption) {
                    this.currentLevel++;
                    if (this.currentLevel <= this.supportedLevel - 1) {
                        levelClassName = `level-${this.currentLevel.toString()}`;
                        /* Last parameter indicated if it is child or not */
                        subMenu = this._renderSubMenu(option.subMenu, isMobileView ? option.id : undefined, true, option.linkText);
                    }
                }
                return (
                    <>
                        <Node {...ListItem} key={option.id} className={`${ListItem.className} ${option.custom_classname}`}>
                            {menuItem}
                            {/* VSI Customization - START */}
                            {isMobileView && subMenu}
                            {/* Following node displays submenu against a menu item when user moves mouse on it on desktop*/}
                            {!isMobileView && (
                                <Node {...MenuList} className={classnames(MenuList.className, 'level-3')}>
                                    <Node {...ListItem} key={option.id} id={option.id}>
                                        {subMenu}
                                    </Node>
                                </Node>
                            )}
                            {/* VSI Customization - END */}
                        </Node>
                    </>
                );
            });

        return this._renderMenu(levelClassName, menuOptions, activeMenu, _subMenus);
    }
    /* VSI Customization - START */
    /* Following function navigate to category page */
    private _gotoCategoryPage = (categoryPageURL: string | undefined) => () => {
        if (categoryPageURL) {
            window.location.href = categoryPageURL;
        }
    };

    /* VSI Customization - END */
    private _renderButtonMenuItem(option: IMenuItemDataExtended, activeMenu?: number, index?: number): JSX.Element | null {
        const { Button, isMobileView } = this.props;
        /* VSI Customization - START */
        //customization to remove Offers category dynamically.
        if (option.linkURL === '/all/offers/5637246576.c') {
            return null;
        }

        if (isMobileView) {
            return (
                <Node
                    key={index}
                    {...Button}
                    className={`${Button.className} ${option.custom_classname}`}
                    onClick={() => {
                        this._handleDropdownToggle(option, activeMenu)();
                    }}
                    aria-haspopup={true}
                    aria-expanded={this.state.activeMenu && this.state.activeMenu === option.id ? true : false}
                    data-parent={activeMenu}
                >
                    <div>{option.linkText}</div>
                </Node>
            );
        } else {
            /* Mouse Hover on menuitem displays its submenu till level 3 */
            /* Mouse Click on menuitem will navigate to its category page */

            return (
                <Node
                    {...Button}
                    className={`${Button.className} ${option.custom_classname}`}
                    key={index}
                    onMouseEnter={this._handleDropdownToggle(option, activeMenu)}
                    onClick={() => {
                        this._gotoCategoryPage(option.linkURL)();
                    }}
                    aria-haspopup={true}
                    aria-expanded={this.state.activeMenu && this.state.activeMenu === option.id ? true : false}
                    data-parent={activeMenu}
                >
                    <div>{option.linkText}</div>
                </Node>
            );
        }
        /* VSI Customization - START */
    }
    private _renderLinkMenuItem(
        option: IMenuItemDataExtended,
        index?: number,
        activeMenu?: number | undefined,
        _isChild?: boolean | undefined,
        parentName?: string
    ): JSX.Element | null {
        const { isMobileView } = this.props;
        const linkText = option.linkText ? option.linkText : '';
        this.payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        //remove <Strong>linktext, when third category gets the same name of it's parent-- #task-11184
        const optionLinkText = _isChild && linkText === parentName ? linkText : linkText;
        /* VSI Customization - START*/
        if (isMobileView) {
            return (
                <Node
                    {...this.props.Link}
                    key={index}
                    href={option.linkURL}
                    {...attributes}
                    onClick={() => {
                        onTelemetryClick(this.telemetryContent, this.payLoad, linkText)();
                    }}
                >
                    {optionLinkText}
                </Node>
            );
        } else {
            if (this.props.config?.removeStaticMenuOnDesktop?.includes(optionLinkText)) {
                return null;
            }
            /* onClick navigates to category page if clicked item is child item (level 3) */
            const onClick = _isChild
                ? {
                      onClick: () => {
                          this._handleLinkClick(option.linkURL)();
                      }
                  }
                : { onClick: () => {} };
            return (
                <Node
                    {...this.props.Link}
                    className={`${this.props.Link.className}`}
                    key={index}
                    href={option.linkURL}
                    {...attributes}
                    onMouseOver={this._handleMouseHover(option.id, _isChild)}
                    {...onClick}
                >
                    {optionLinkText}
                    {!_isChild && option.subMenu && option.subMenu.length > 0 ? <Node className='ms-expand-arrow' /> : undefined}
                </Node>
            );
        }
        /* VSI Customization - END - 31/10/20 */
    }

    /* VSI Customization - START */
    /* Following function sets value of previous current category in case of level 3 and option.id in case of level 2 */
    private _handleMouseHover = (optionID: number | undefined, _isChild?: boolean | undefined) => () => {
        const _activeItem = _isChild ? this.state.currentCategory : optionID;
        _activeItem !== undefined && this.setState({ currentCategory: _activeItem });
    };
    /* onClick navigates to category page if clicked item is child item */
    private _handleLinkClick = (linkURL: string | undefined) => () => {
        this._gotoCategoryPage(linkURL);
    };
    /* VSI Customization - END */
    private _renderSpanMenuItem(option: IMenuItemDataExtended, index?: number): JSX.Element | null {
        const { Span } = this.props;
        return (
            <Node key={index} {...Span}>
                {option.linkText}
            </Node>
        );
    }
    // VSI Customization - Added categoryImageLink has been added
    private _renderMenu(level: string, menuOptions: JSX.Element[], currentItem?: number, _subMenus?: IMenuItemData[]): JSX.Element | null {
        const { DivContainer, MenuList, ImageDivContainer, showCategoryImage } = this.props;
        const { categoryImage, categoryImageLink } = this.state;
        this.currentLevel = 1;
        const scheduledImageUrl = this._getCategoryScheduledImageUrl() || categoryImageLink;
        if (this.props.isMobileView && showCategoryImage && _subMenus) {
            return (
                <Node {...DivContainer} className={DivContainer!.className}>
                    <Node {...MenuList} className={classnames(MenuList.className, level)}>
                        {menuOptions}
                    </Node>
                    <Node {...ImageDivContainer} className={'menu-item-image'}>
                        {categoryImage !== undefined && categoryImage !== null && (
                            <>
                                {scheduledImageUrl ? (
                                    <a href={scheduledImageUrl}>{this._getCategoryImage(categoryImage)}</a>
                                ) : (
                                    this._getCategoryImage(categoryImage)
                                )}
                            </>
                        )}
                    </Node>
                </Node>
            );
        } else if (!this.props.isMobileView && showCategoryImage) {
            return (
                <Node {...DivContainer} className={DivContainer!.className}>
                    <Node {...MenuList} className={classnames(MenuList.className, level)}>
                        {menuOptions}
                    </Node>
                    <Node {...ImageDivContainer} className={'menu-item-image'}>
                        {categoryImage !== undefined && categoryImage !== null && (
                            <>
                                {scheduledImageUrl ? (
                                    <a href={scheduledImageUrl}>{this._getCategoryImage(categoryImage)}</a>
                                ) : (
                                    this._getCategoryImage(categoryImage)
                                )}
                            </>
                        )}
                    </Node>
                </Node>
            );
        }
        return (
            <Node {...DivContainer} className={DivContainer!.className}>
                <Node {...MenuList} className={classnames(MenuList.className, level)} id={currentItem}>
                    {menuOptions}
                </Node>
            </Node>
        );
    }

    /* VSI Customization - START - 04/01/21 */
    // private _getSubMenuLength = (menuItemId: number | undefined) => {
    //     const menuItem = menuItemId && menuItemId.toString();
    //     const htmlElement = menuItem && this.documentRef && this.documentRef.getElementById(menuItem);
    //     const menuItemHeight = htmlElement && htmlElement.offsetHeight;
    //     return menuItemHeight === 0 ? undefined : menuItemHeight;
    // };
    /* VSI Customization - END */

    private _handleDropdownToggle = (data: IMenuItemData, parentId?: number) => () => {
        if (!this.props.isMobileView) {
            this.setState({
                activeMenu: this.state.activeMenu && this.state.activeMenu === data.id! ? undefined : data.id!,
                parentMenu: parentId,
                currentCategory: undefined // parent of third menu to render third level menu
            });
            if (this.props.showCategoryImage) {
                this._updateCategoryImage(data.imageSource!, data.linkText!)();
            }
        } else {
            NavigationMenuView.isBackTrack = false;
            if (this.props.showCategoryImage && this.currentLevelMobile === 0) {
                this._updateCategoryImage(data.imageSource!, data.linkText!)();
            }
            this.setState({
                activeMenu: data.id,
                mobileViewLabelText: data.linkText!,
                parentMenu: parentId,
                isBackBtnClicked: false
            });
        }

        this._resetFocus();
    };

    private _handleGoBack = () => () => {
        NavigationMenuView.isBackTrack = true;
        this.setState({ activeMenu: this.state.parentMenu, isBackBtnClicked: true });
        /* VSI Customization - START -13/11/20 */
        // decrement of 1 in currentLevelMobile when user clicks on back button
        this.currentLevelMobile--;
        /* VSI Customization - END */
        this._resetFocus();
    };

    private _resetFocus = () => {
        if (this.props.isMobileView) {
            setTimeout(() => {
                this.menuNode && this.menuNode.current && this.menuNode.current.focus();
            }, 0);
        }
    };

    // tslint:disable-next-line:no-any
    private _handleClickOutside = (event: any) => {
        if (this.menuNode.current && !this.menuNode.current.contains(event.target)) {
            this.setState({ activeMenu: undefined, mobileViewLabelText: '' });
        }
    };

    private _closeSubmenu(): void {
        if (!this.props.isMobileView) {
            this.setState({ activeMenu: undefined, mobileViewLabelText: '' });
        }
    }

    private isInViewport = () => {
        const bounding = this.navRef.current?.getBoundingClientRect();
        return bounding && bounding.top >= 0;
    };

    /* VSI Customization - START - 29/10/20 */
    /* Sticky Navigation function added */
    private _mainMenuScroll = (): void => {
        if (this.isInViewport()) {
            this.setState({
                scrollClass: false,
                navigationSearch: true
            });
        } else if (!this.state.scrollClass) {
            this.setState({
                scrollClass: true,
                navigationSearch: true
            });
        }
    };
    /* VSI Customization - END - 29/10/20 */
}

export default NavigationMenuView;
